import { api } from '../api.js';
import { esc, money, dateTime, dialog, toast, guard } from '../ui.js';
import { shrinkImage, postToS3 } from '../upload.js';

export async function experiencesView(root, me) {
  const canEdit = me.groups.some((g) => g === 'Owner' || g === 'Manager');
  const canCover = canEdit || me.groups.includes('ContentEditor');
  let staff = [];
  if (canEdit) staff = (await api('/staff')).staff.filter((s) => s.active);
  let selected = null;

  const render = guard(async () => {
    const { experiences, slots } = await api('/experiences');
    if (!selected && experiences.length) selected = experiences[0].id;
    const exp = experiences.find((e) => e.id === selected);
    root.innerHTML = `
      <div class="page-head"><h1>Experiences &amp; dates</h1>
        ${canEdit ? '<button class="btn small" type="button" id="new-exp">+ New experience</button>' : ''}</div>
      <div class="split">
        <div class="box" style="flex:1 1 260px;max-width:360px;padding:0">
          <div class="list">${experiences.map((e) => `<button type="button" data-exp="${e.id}" aria-current="${e.id === selected}">
            <span>${esc(e.name)}</span>${e.active ? '' : '<span class="badge">Hidden</span>'}</button>`).join('') || '<p class="muted" style="padding:16px">No experiences yet.</p>'}</div>
        </div>
        <div style="flex:999 1 520px;display:flex;flex-direction:column;gap:22px">${exp ? detail(exp, slots.filter((s) => s.experience_id === exp.id)) : ''}</div>
      </div>`;
  });

  function detail(e, slots) {
    const dis = canEdit ? '' : 'disabled';
    const cover = e.cover_key ? `/${e.cover_key}` : null;
    return `<div class="box" id="cover-box">
        <div class="page-head"><h2>Cover photo</h2>
          ${canCover ? `<div class="toolbar">
            <label class="btn small primary" style="cursor:pointer">${cover ? 'Replace photo' : 'Upload photo'}<input type="file" id="cover-file" accept="image/jpeg,image/png,image/webp" class="hidden"></label>
            ${cover ? '<button class="btn small danger" type="button" id="cover-remove">Remove</button>' : ''}</div>` : ''}</div>
        <div class="photo cover-preview">${cover ? `<img src="${esc(cover)}" alt="${esc(e.name)} cover photo">` : '<span class="label">No cover photo yet. The website shows a placeholder.</span>'}</div>
        <div id="cover-status" role="status"></div>
        <p class="small muted">Shown on this experience's card on the website. Landscape photos work best; the card crops to a wide frame. Large phone photos are resized automatically.</p>
      </div>
      <form class="box" id="exp-form" novalidate>
        <h2>Details</h2>
        <label class="field"><span>Name</span><input name="name" value="${esc(e.name)}" required ${dis}></label>
        <label class="field"><span>Description (shown on the website)</span><textarea name="description" rows="4" ${dis}>${esc(e.description)}</textarea></label>
        <div class="form-grid">
          <label class="field"><span>Category</span><input name="category" value="${esc(e.category)}" ${dis}></label>
          <label class="field"><span>Price ($)</span><input name="price" type="number" min="0" step="0.01" value="${(e.price_cents / 100).toFixed(2)}" required ${dis}></label>
          <label class="field"><span>Priced</span><select name="pricingUnit" ${dis}>
            <option value="per_guest" ${e.pricing_unit === 'per_guest' ? 'selected' : ''}>Per guest</option>
            <option value="per_group" ${e.pricing_unit === 'per_group' ? 'selected' : ''}>Per group</option></select></label>
          <label class="field"><span>Deposit %</span><input name="depositPct" type="number" min="0" max="100" value="${e.deposit_pct}" ${dis}></label>
        </div>
        <label class="check"><input type="checkbox" name="active" ${e.active ? 'checked' : ''} ${dis}><span>Show on the website</span></label>
        ${canEdit ? '<div><button class="btn small primary" type="submit">Save details</button></div>' : ''}
      </form>
      <div class="box">
        <div class="page-head"><h2>Dates</h2>${canEdit ? '<button class="btn small" type="button" id="add-date">+ Add date</button>' : ''}</div>
        ${slots.length ? `<div class="table-wrap"><table class="data"><thead><tr><th>Date</th><th>Booked</th><th>Capacity</th><th>Guide</th><th>Status</th><th></th></tr></thead><tbody>
          ${slots.map((s) => `<tr data-slot="${s.id}">
            <td>${esc(dateTime(s.starts_at))}<div class="small muted">${esc(s.pickup_location)}</div></td>
            <td>${s.capacity - s.seats_left}</td>
            <td><input type="number" min="0" max="500" value="${s.capacity}" data-field="capacity" style="width:90px" ${dis} aria-label="Capacity"></td>
            <td><select data-field="guideStaffId" ${dis} aria-label="Guide"><option value="">None</option>
              ${staff.map((p) => `<option value="${p.id}" ${p.id === s.guide_staff_id ? 'selected' : ''}>${esc(p.email)}</option>`).join('')}
              ${!canEdit && s.guide_email ? `<option selected>${esc(s.guide_email)}</option>` : ''}</select></td>
            <td><select data-field="status" ${dis} aria-label="Status">${['open', 'closed', 'cancelled'].map((o) => `<option ${o === s.status ? 'selected' : ''}>${o}</option>`).join('')}</select></td>
            <td>${canEdit ? '<button class="btn small" type="button" data-save>Save</button>' : ''}</td></tr>`).join('')}
        </tbody></table></div>` : '<p class="muted">No dates yet.</p>'}
        <p class="small muted">To show a date as <strong>Sold out</strong> on the website, set its capacity to the number already booked (or 0). "Closed" hides the date from the website but keeps existing bookings. Price per guest: ${money(e.price_cents)}.</p>
      </div>`;
  }

  root.addEventListener('click', guard(async (ev) => {
    const pick = ev.target.closest('[data-exp]');
    if (pick) { selected = pick.dataset.exp; return render(); }
    if (ev.target.closest('#cover-remove')) {
      if (!confirm('Remove the cover photo? The website will show a placeholder.')) return;
      await api(`/experiences/${selected}/cover`, { method: 'DELETE' });
      toast('Cover photo removed');
      return render();
    }
    if (ev.target.closest('#new-exp')) {
      const f = await dialog(`<h2>New experience</h2>
        <label class="field"><span>Name</span><input name="name" required></label>
        <label class="field"><span>Price per guest ($)</span><input name="price" type="number" min="0" step="0.01" required></label>
        <div data-error></div>
        <div class="dialog-actions"><button class="btn small" value="cancel" formnovalidate>Back</button><button class="btn small primary" value="ok">Create</button></div>`);
      if (!f) return;
      const row = await api('/experiences', { method: 'POST', body: { name: f.get('name'), priceCents: Math.round(Number(f.get('price')) * 100), active: false } });
      selected = row.id;
      toast('Created. It stays hidden from the website until you tick "Show on the website".');
      return render();
    }
    if (ev.target.closest('#add-date')) {
      const f = await dialog(`<h2>Add a date</h2>
        <label class="field"><span>Date and pickup time (Pacific)</span><input name="startsAt" type="datetime-local" required></label>
        <label class="field"><span>Pickup location</span><input name="pickupLocation"></label>
        <label class="field"><span>Seats</span><input name="capacity" type="number" min="1" max="500" value="12" required></label>
        <div data-error></div>
        <div class="dialog-actions"><button class="btn small" value="cancel" formnovalidate>Back</button><button class="btn small primary" value="ok">Add date</button></div>`);
      if (!f) return;
      await api(`/experiences/${selected}/slots`, { method: 'POST', body: {
        startsAt: new Date(f.get('startsAt')).toISOString(), pickupLocation: f.get('pickupLocation'), capacity: Number(f.get('capacity')),
      } });
      toast('Date added');
      return render();
    }
    const save = ev.target.closest('[data-save]');
    if (save) {
      const tr = save.closest('tr');
      const v = (n) => tr.querySelector(`[data-field="${n}"]`).value;
      await api(`/slots/${tr.dataset.slot}`, { method: 'PUT', body: { capacity: Number(v('capacity')), status: v('status'), guideStaffId: v('guideStaffId') || null } });
      toast('Date saved');
      return render();
    }
  }));

  root.addEventListener('change', guard(async (ev) => {
    if (ev.target.id !== 'cover-file' || !ev.target.files.length) return;
    const status = root.querySelector('#cover-status');
    status.innerHTML = '<p class="notice">Uploading…</p>';
    try {
      const file = await shrinkImage(ev.target.files[0]);
      const { key, upload } = await api(`/experiences/${selected}/cover-upload`, { method: 'POST', body: { contentType: file.type } });
      await postToS3(upload, file);
      await api(`/experiences/${selected}/cover`, { method: 'PUT', body: { key } });
      toast('Cover photo updated. It shows on the website right away.');
    } catch (err) {
      status.innerHTML = '';
      throw err;
    }
    render();
  }));

  root.addEventListener('submit', guard(async (ev) => {
    if (ev.target.id !== 'exp-form') return;
    ev.preventDefault();
    const f = new FormData(ev.target);
    await api(`/experiences/${selected}`, { method: 'PUT', body: {
      name: f.get('name'), description: f.get('description'), category: f.get('category'),
      priceCents: Math.round(Number(f.get('price')) * 100), pricingUnit: f.get('pricingUnit'),
      depositPct: Number(f.get('depositPct')), active: f.get('active') === 'on',
    } });
    toast('Saved. The website shows the change right away.');
    render();
  }));

  render();
}
